import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import React, { useCallback, useEffect, useState } from 'react';
import {
  clearImageFailure,
  getCachedBlobUrl,
  getCachedImageStatus,
  getCachedImageUrl,
  markImageFailed,
  markImageLoaded,
  retryImageFetch,
  subscribeToImageCache,
  ZoomMeta,
} from '../../hooks/useZoomPreview';
import { MasterCropData } from '../../types';
import {
  CropStatusBadge,
  getCropOrientationBadgeLabel,
  getCropOrientationBadgeStyles,
  getCropSourceBadgeLabel,
  getCropSourceBadgeStyles,
  softenBadgeFill,
} from './CropStatusBadge';

export interface CropThumbnailProps {
  crop: MasterCropData;
  sectionType: 'front' | 'non-front' | 'carton';
  size?: number;
  selectionVisualMode?: 'default' | 'master-label';
  isSelected?: boolean;
  isRepresentative?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  isPending?: boolean;
  showDeletionMark?: boolean;
  isLinkHighlighted?: boolean;
  highlightColor?: string;
  highlightGlow?: string;
  showTriangle?: boolean;
  isRecentlyMoved?: boolean;
  reloadVersion?: number;
  topLeftBadgeMode?: 'number' | 'warning' | null;
  topLeftBadgeLabel?: string;
  topLeftBadgeBackground?: string;
  topLeftBadgeBorder?: string;
  topLeftBadgeColor?: string;
  sourceBadgeLabel?: 'S' | 'F' | 'C' | 'A' | 'O';
  onClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}

function getReviewIssueBadgeLabel(
  reviewIssueType?: MasterCropData['reviewIssueType']
): 'D' | 'S' | 'B' | 'O' | null {
  if (reviewIssueType === 'missing_design') return 'D';
  if (reviewIssueType === 'sop_unclear') return 'S';
  if (reviewIssueType === 'bad_quality') return 'B';
  if (reviewIssueType === 'other') return 'O';
  return null;
}

const MASTER_LABEL_SELECTED_BORDER_COLOR = 'rgba(249,115,22,0.94)';
const MASTER_LABEL_SELECTED_RING = 'rgba(249,115,22,0.62)';
const MASTER_LABEL_MOVED_RING = 'rgba(63,185,80,0.88)';
const MASTER_LABEL_MOVED_GLOW = 'rgba(63,185,80,0.78)';
const MASTER_LABEL_MOVED_FILTER_BASE =
  'drop-shadow(0 0 6px rgba(63,185,80,0.38)) drop-shadow(0 0 14px rgba(63,185,80,0.28))';
const MASTER_LABEL_MOVED_ANIMATION_NAME = 'master-label-moved-glow';
const MASTER_LABEL_MOVED_KEYFRAMES_ID = 'master-label-moved-glow-keyframes';
const LINK_HIGHLIGHT_BORDER_WIDTH = '3.3px';
const REMOTE_DC_DRAFT_BORDER_COLOR = '#a855f7';

export const CropThumbnail: React.FC<CropThumbnailProps> = ({
  crop,
  sectionType,
  size = 46,
  selectionVisualMode = 'default',
  isSelected = false,
  isRepresentative = false,
  isFocused = false,
  isDisabled = false,
  isPending = false,
  showDeletionMark = false,
  isLinkHighlighted = false,
  highlightColor = 'var(--purple)',
  highlightGlow = '0 0 0 3px rgba(188,140,255,0.46), 0 0 18px rgba(188,140,255,0.88), 0 0 36px rgba(188,140,255,0.42)',
  showTriangle = false,
  isRecentlyMoved = false,
  reloadVersion = 0,
  topLeftBadgeMode = null,
  topLeftBadgeLabel,
  topLeftBadgeBackground,
  topLeftBadgeBorder,
  topLeftBadgeColor,
  sourceBadgeLabel,
  onClick,
  onContextMenu,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const isInactive = crop.isInactive;
  const cropPreview = crop.cropPreview;
  const displayImageUrl = cropPreview?.sourceImageUrl || crop.imageUrl;
  const [imgError, setImgError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => getCachedImageUrl(displayImageUrl));

  const meta: ZoomMeta = {
    id: crop.id,
    storeId: crop.storeId,
    source: crop.source,
    forgeOriginSource: crop.forgeOriginSource,
    barcode: crop.barcode,
    normalizedBarcode: crop.normalizedBarcode,
    normalizedUpc: crop.normalizedUpc,
    classificationType: crop.classificationType,
    warningMessage:
      topLeftBadgeMode === 'warning' ? 'Barcode does not match known barcodes' : undefined,
    imageResolution: crop.imageResolution,
    createdBy: crop.createdBy,
    createdAt: crop.createdAt,
    updatedBy: crop.updatedBy,
    updatedAt: crop.updatedAt,
  };

  const handleImgError = useCallback(() => {
    if (getCachedImageStatus(displayImageUrl) === 'pending') {
      setRetrying(true);
      return;
    }
    markImageFailed(displayImageUrl);
    setImgError(true);
    setRetrying(false);
  }, [displayImageUrl]);

  const handleImgLoad = useCallback(() => {
    markImageLoaded(displayImageUrl);
    setImgError(false);
    setRetrying(false);
  }, [displayImageUrl]);

  const handleRetry = useCallback(
    (e: React.MouseEvent) => {
      setRetrying(true);
      clearImageFailure(displayImageUrl);
      setImgError(false);
      setImageSrc(getCachedBlobUrl(displayImageUrl) ?? displayImageUrl);
      retryImageFetch(displayImageUrl, true);
    },
    [displayImageUrl]
  );

  useEffect(() => {
    setImageSrc(getCachedBlobUrl(displayImageUrl) ?? displayImageUrl);
    setImgError(false);
    setRetrying(false);
    setRetrying(getCachedImageStatus(displayImageUrl) === 'pending');

    const unsubscribe = subscribeToImageCache(displayImageUrl, () => {
      const blobUrl = getCachedBlobUrl(displayImageUrl);
      if (blobUrl) {
        setImageSrc(blobUrl);
        setImgError(false);
        setRetrying(false);
        return;
      }

      if (getCachedImageStatus(displayImageUrl) === 'pending') {
        setRetrying(true);
        return;
      }

      if (getCachedImageStatus(displayImageUrl) === 'failed') {
        setRetrying(false);
        setImgError(true);
      }
    });

    return unsubscribe;
  }, [displayImageUrl]);

  useEffect(() => {
    if (reloadVersion === 0) return;
    clearImageFailure(displayImageUrl);
    retryImageFetch(displayImageUrl, true);
    setImgError(false);
    setRetrying(true);
    setImageSrc(getCachedBlobUrl(displayImageUrl) ?? displayImageUrl);
  }, [reloadVersion, displayImageUrl]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById(MASTER_LABEL_MOVED_KEYFRAMES_ID)) return;

    const style = document.createElement('style');
    style.id = MASTER_LABEL_MOVED_KEYFRAMES_ID;
    style.textContent = `
      @keyframes ${MASTER_LABEL_MOVED_ANIMATION_NAME} {
        0%, 100% {
          filter: drop-shadow(0 0 6px rgba(63,185,80,0.38))
            drop-shadow(0 0 14px rgba(63,185,80,0.28));
        }
        50% {
          filter: drop-shadow(0 0 12px rgba(63,185,80,0.62))
            drop-shadow(0 0 30px rgba(63,185,80,0.48));
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button === 0) {
      e.preventDefault();
    }
  }, []);

  const triangleColor = crop.isBadImage
    ? 'var(--danger)'
    : sectionType === 'non-front' || sectionType === 'carton'
      ? 'var(--warning)'
      : crop.isMismatch
        ? 'var(--warning)'
        : 'var(--positive)';

  const triSize = size > 72 ? 16 : 14;
  const iconSize = Math.max(14, Math.round(size * 0.35));
  const cropPreviewScale = cropPreview
    ? Math.min(size / cropPreview.cropWidth, size / cropPreview.cropHeight)
    : 1;
  const renderedCropWidth = cropPreview ? cropPreview.cropWidth * cropPreviewScale : size;
  const renderedCropHeight = cropPreview ? cropPreview.cropHeight * cropPreviewScale : size;
  const renderedCropLeft = cropPreview ? (size - renderedCropWidth) / 2 : 0;
  const renderedCropTop = cropPreview ? (size - renderedCropHeight) / 2 : 0;
  const isImageLoading = retrying && !imgError;
  const isFiveMp = crop.imageResolution?.toLowerCase() === '5mp';
  const reviewIssueBadgeLabel = showDeletionMark
    ? null
    : getReviewIssueBadgeLabel(crop.reviewIssueType);
  const showReviewDeletionMark = !showDeletionMark && crop.reviewIssueType === 'mark_for_deletion';
  const reviewIssueBadgeSize = Math.max(14, Math.round(size * 0.24));
  const loadingBadgeSize = Math.max(14, Math.round(size * 0.28));
  const showFocusStyle = isLinkHighlighted || isFocused;
  const focusShadow = isLinkHighlighted
    ? highlightGlow
    : '0 0 0 3px rgba(188,140,255,0.4), 0 0 16px rgba(188,140,255,0.78), 0 0 32px rgba(188,140,255,0.36)';
  const reviewBadgeTopOffset = 3;
  const loadingTopOffset = reviewIssueBadgeLabel ? reviewIssueBadgeSize + 6 : 3;
  const fiveMpTopOffset = isImageLoading
    ? loadingTopOffset + loadingBadgeSize + 3
    : reviewIssueBadgeLabel
      ? reviewIssueBadgeSize + 6
      : isRepresentative
        ? 16
        : 3;
  const resolvedSourceBadgeLabel = sourceBadgeLabel || getCropSourceBadgeLabel(crop.source);
  const sourceBadgeStyles = getCropSourceBadgeStyles(resolvedSourceBadgeLabel);
  const showOrientationBadge = crop.classificationType !== 'carton' && sectionType !== 'carton';
  const orientationBadgeLabel = getCropOrientationBadgeLabel(crop.orientation);
  const orientationBadgeStyles = getCropOrientationBadgeStyles(orientationBadgeLabel);
  const isMasterLabelSelected = isSelected && selectionVisualMode === 'master-label';
  const isRemoteDataCollectionDraft =
    crop.forgeOriginSource === 'remote-data-collection' || crop.id.startsWith('remote-dc:');
  const selectedShadow = isMasterLabelSelected
    ? `0 0 0 3px ${MASTER_LABEL_SELECTED_RING}, 0 10px 24px rgba(15, 23, 42, 0.24)`
    : 'var(--crop-selected-shadow)';
  const movedShadow =
    selectionVisualMode === 'master-label' && isRecentlyMoved
      ? `0 0 0 3px ${MASTER_LABEL_MOVED_RING}, 0 0 26px ${MASTER_LABEL_MOVED_GLOW}`
      : null;
  const resolvedBorderWidth = isSelected
    ? isMasterLabelSelected
      ? '1.4px'
      : 'var(--crop-selected-border-width)'
    : showFocusStyle
      ? LINK_HIGHLIGHT_BORDER_WIDTH
      : isInactive || isPending
        ? '1px'
        : '2px';
  const resolvedBorderStyle = isSelected
    ? 'solid'
    : showFocusStyle
      ? 'solid'
      : isInactive || isPending
        ? 'dashed'
        : 'solid';
  const resolvedBorderColor = isSelected
    ? isMasterLabelSelected
      ? MASTER_LABEL_SELECTED_BORDER_COLOR
      : 'var(--crop-selected-border)'
    : showFocusStyle
      ? isLinkHighlighted
        ? highlightColor
        : 'var(--purple)'
      : isRemoteDataCollectionDraft
        ? REMOTE_DC_DRAFT_BORDER_COLOR
        : isInactive || isPending
          ? 'var(--text-tertiary)'
          : 'var(--border)';
  const resolvedBoxShadow = isSelected
    ? movedShadow
      ? `${selectedShadow}, ${movedShadow}`
      : selectedShadow
    : showFocusStyle
      ? focusShadow
      : movedShadow || 'none';
  const resolvedAnimation = isRecentlyMoved
    ? selectionVisualMode === 'master-label'
      ? `${MASTER_LABEL_MOVED_ANIMATION_NAME} 2.1s ease-in-out infinite`
      : 'cropLanded 1.8s ease-out forwards'
    : showFocusStyle
      ? 'pulse-glow 1.5s ease-in-out infinite'
      : undefined;
  const resolvedFilter =
    selectionVisualMode === 'master-label' && isRecentlyMoved
      ? MASTER_LABEL_MOVED_FILTER_BASE
      : undefined;
  const resolvedTransform = isSelected
    ? isMasterLabelSelected
      ? 'scale(1.06) translateY(-1px)'
      : undefined
    : undefined;
  const resolvedZIndex = isSelected ? (isMasterLabelSelected ? 3 : undefined) : undefined;

  return (
    <div
      data-crop-id={crop.id}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseDown={handleMouseDown}
      onMouseEnter={onImageHover ? (e) => onImageHover(e, displayImageUrl, meta) : undefined}
      onMouseMove={onImageMove ? (e) => onImageMove(e, displayImageUrl, meta) : undefined}
      onMouseLeave={onImageLeave}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        cursor: isDisabled ? 'default' : 'pointer',
        borderWidth: resolvedBorderWidth,
        borderStyle: resolvedBorderStyle,
        borderColor: resolvedBorderColor,
        boxShadow: resolvedBoxShadow,
        opacity: isDisabled ? 0.5 : 1,
        transition: 'all 120ms ease',
        flexShrink: 0,
        animation: resolvedAnimation,
        filter: resolvedFilter,
        transform: resolvedTransform,
        zIndex: resolvedZIndex,
        minWidth: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {imgError ? (
        <div
          onClick={handleRetry}
          role="button"
          aria-label="Click to retry"
          style={{
            width: '100%',
            height: '100%',
            background: '#1a1a1a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            cursor: 'pointer',
          }}
        >
          <BrokenImageOutlinedIcon
            style={{
              fontSize: iconSize,
              color: retrying ? 'var(--accent)' : 'var(--text-tertiary)',
              opacity: retrying ? 1 : 0.5,
              animation: retrying ? 'pulse-glow 1s ease-in-out infinite' : undefined,
            }}
          />
        </div>
      ) : (
        <>
          {cropPreview ? (
            <div
              data-testid={`crop-preview-window-${crop.id}`}
              style={{
                position: 'absolute',
                left: renderedCropLeft,
                top: renderedCropTop,
                width: renderedCropWidth,
                height: renderedCropHeight,
                overflow: 'hidden',
                background: '#000',
                pointerEvents: 'none',
              }}
            >
              <img
                src={imageSrc}
                alt={crop.id}
                loading="lazy"
                decoding="async"
                style={{
                  position: 'absolute',
                  width: cropPreview.sourceWidth * cropPreviewScale,
                  height: cropPreview.sourceHeight * cropPreviewScale,
                  left: -cropPreview.cropX * cropPreviewScale,
                  top: -cropPreview.cropY * cropPreviewScale,
                  maxWidth: 'none',
                  objectFit: 'fill',
                  background: '#000',
                  display: 'block',
                  pointerEvents: 'none',
                }}
                draggable={false}
                onLoad={handleImgLoad}
                onError={handleImgError}
              />
            </div>
          ) : (
            <img
              src={imageSrc}
              alt={crop.id}
              loading="lazy"
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                background: '#000',
                display: 'block',
                pointerEvents: 'none',
              }}
              draggable={false}
              onLoad={handleImgLoad}
              onError={handleImgError}
            />
          )}
        </>
      )}
      {!imgError && cropPreview?.polygonPoints?.length ? (
        <svg
          data-testid={`crop-preview-polygon-${crop.id}`}
          viewBox={`0 0 ${size} ${size}`}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <polygon
            points={cropPreview.polygonPoints
              .map(
                (point) =>
                  `${renderedCropLeft + point.x * cropPreviewScale},${
                    renderedCropTop + point.y * cropPreviewScale
                  }`
              )
              .join(' ')}
            fill="rgba(249, 115, 22, 0.18)"
            stroke="rgba(249, 115, 22, 0.98)"
            strokeWidth={Math.max(2, Math.round(size * 0.035))}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      ) : null}
      {showDeletionMark && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(248, 81, 73, 0.12)',
              pointerEvents: 'none',
            }}
          />
          <div
            data-testid={`crop-pending-deletion-${crop.id}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(5, Math.round(size * 0.1)),
                background: 'rgba(248, 81, 73, 0.96)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(45deg)',
                boxShadow: '0 0 12px rgba(248,81,73,0.45)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(5, Math.round(size * 0.1)),
                background: 'rgba(248, 81, 73, 0.96)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(-45deg)',
                boxShadow: '0 0 12px rgba(248,81,73,0.45)',
              }}
            />
          </div>
        </>
      )}
      {showReviewDeletionMark && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(107, 114, 128, 0.12)',
              pointerEvents: 'none',
            }}
          />
          <div
            data-testid={`crop-review-mark-for-deletion-${crop.id}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(4, Math.round(size * 0.085)),
                background: 'rgba(107, 114, 128, 0.92)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(45deg)',
                boxShadow: '0 0 10px rgba(75,85,99,0.38)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '-12%',
                width: '124%',
                height: Math.max(4, Math.round(size * 0.085)),
                background: 'rgba(107, 114, 128, 0.92)',
                borderRadius: 999,
                transform: 'translateY(-50%) rotate(-45deg)',
                boxShadow: '0 0 10px rgba(75,85,99,0.38)',
              }}
            />
          </div>
        </>
      )}
      {isImageLoading && (
        <div
          style={{
            position: 'absolute',
            top: loadingTopOffset,
            right: 3,
            width: loadingBadgeSize,
            height: loadingBadgeSize,
            borderRadius: 999,
            background: softenBadgeFill('rgba(15, 15, 15, 0.78)'),
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
          aria-label="Image loading"
        >
          <AutorenewOutlinedIcon
            style={{
              fontSize: Math.max(10, Math.round(size * 0.18)),
              color: 'var(--accent)',
              animation: 'forge-spin 0.9s linear infinite',
            }}
          />
        </div>
      )}
      {reviewIssueBadgeLabel && (
        <div
          data-testid={`crop-review-issue-${crop.id}`}
          aria-label={`Review issue ${reviewIssueBadgeLabel}`}
          style={{
            position: 'absolute',
            top: reviewBadgeTopOffset,
            right: 3,
            minWidth: reviewIssueBadgeSize,
            height: reviewIssueBadgeSize,
            padding: '0 4px',
            borderRadius: 999,
            background: softenBadgeFill('rgba(201, 45, 66, 0.96)'),
            border: '1px solid rgba(255, 162, 172, 0.58)',
            color: '#fff5f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(7, Math.round(size * 0.12)),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.02em',
          }}
        >
          {reviewIssueBadgeLabel}
        </div>
      )}
      {isFiveMp && (
        <div
          style={{
            position: 'absolute',
            top: fiveMpTopOffset,
            right: 3,
            minWidth: Math.max(15, Math.round(size * 0.36)),
            height: Math.max(9, Math.round(size * 0.17)),
            padding: '0 3px',
            borderRadius: 999,
            background: softenBadgeFill('rgba(201, 45, 66, 0.96)'),
            border: '1px solid rgba(255, 162, 172, 0.58)',
            color: '#fff5f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(5, Math.round(size * 0.1)),
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '0.03em',
          }}
          aria-label="5MP crop"
        >
          5MP
        </div>
      )}
      {topLeftBadgeMode && (
        <div
          style={{
            position: 'absolute',
            left: 3,
            top: 3,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              minWidth: Math.max(10, Math.round(size * 0.19)),
              height: Math.max(10, Math.round(size * 0.19)),
              padding: '0 2px',
              borderRadius: 999,
              background:
                topLeftBadgeMode === 'number'
                  ? softenBadgeFill(topLeftBadgeBackground || 'rgba(88, 166, 255, 0.92)')
                  : softenBadgeFill('rgba(210, 153, 34, 0.96)'),
              border:
                topLeftBadgeMode === 'number'
                  ? topLeftBadgeBorder || '1px solid rgba(145, 193, 255, 0.55)'
                  : '1px solid rgba(255, 220, 120, 0.6)',
              color: topLeftBadgeMode === 'number' ? topLeftBadgeColor || '#f3f9ff' : '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: Math.max(7, Math.round(size * 0.11)),
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {topLeftBadgeMode === 'warning' ? (
              <WarningAmberOutlinedIcon
                style={{ fontSize: Math.max(7, Math.round(size * 0.13)) }}
              />
            ) : (
              topLeftBadgeLabel || '?'
            )}
          </div>
        </div>
      )}
      <CropStatusBadge
        label={resolvedSourceBadgeLabel}
        size={size}
        style={{
          position: 'absolute',
          left: 3,
          bottom: 3,
          ...sourceBadgeStyles,
        }}
      />
      <div
        style={{
          position: 'absolute',
          right: 3,
          bottom: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          pointerEvents: 'none',
        }}
      >
        {showOrientationBadge && (
          <CropStatusBadge
            label={orientationBadgeLabel}
            size={size}
            style={orientationBadgeStyles}
          />
        )}
      </div>
      {/* Crop type triangle (demo only) */}
      {showTriangle && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            borderTop: `${triSize}px solid ${triangleColor}`,
            borderRight: `${triSize}px solid transparent`,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Representative star */}
      {isRepresentative && (
        <StarIcon
          data-testid={`crop-representative-star-${crop.id}`}
          style={{
            position: 'absolute',
            top: 2,
            right: reviewIssueBadgeLabel ? reviewIssueBadgeSize + 5 : 2,
            fontSize: 12,
            color: '#E8976C',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.5))',
          }}
        />
      )}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(232,151,108,0.15)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};
